import { useShowAlert } from '~/composables/use-show-alert'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import type Player from 'video.js/dist/types/player'
import { useState } from '#app'
type VideoJsFactory = typeof import('video.js').default

const MAX_VIDEO_WIDTH_PX = 1100
const VIDEO_VIEWPORT_PADDING_RATIO = 0.95
const FALLBACK_VIDEO_ASPECT_RATIO = 16 / 9
const NORMAL_PLAYBACK_RATE = 1
const FORWARD_SEEK_GRACE_SECONDS = 30
const PLAYBACK_POSITION_EPSILON_SECONDS = 0.5

function inferVideoMimeType(url: string): string | undefined {
    const normalizedUrl = url.split('?')[0].toLowerCase()

    if (normalizedUrl.endsWith('.m3u8')) {
        return 'application/x-mpegURL'
    }

    if (normalizedUrl.endsWith('.mpd')) {
        return 'application/dash+xml'
    }

    if (normalizedUrl.endsWith('.webm')) {
        return 'video/webm'
    }

    if (normalizedUrl.endsWith('.ogg') || normalizedUrl.endsWith('.ogv')) {
        return 'video/ogg'
    }

    if (normalizedUrl.endsWith('.mp4') || normalizedUrl.endsWith('.m4v')) {
        return 'video/mp4'
    }

    return undefined
}

export function useAgreementVideo(
    videoUrl: string | null,
    allowSkippingVideo: boolean,
    validateContinue: () => boolean,
    handleContinue: () => void
) {
    // This is so if they cancel out of the login modal, they do not have to watch the video again
    let alreadyWatchedVideo = false
    let hasHandledContinue = false

    const isWatchingVideo = useState('isWatchingWistiaVideo', () => false)
    isWatchingVideo.value = false
    const isLoadingVideo = ref(false)

    let videoPlayer: Player | undefined = undefined
    let videoJsFactory: VideoJsFactory | undefined = undefined
    let videoJsLoadPromise: Promise<VideoJsFactory> | undefined = undefined
    let playerReadyPromise: Promise<void> | undefined = undefined
    let completionPositionSeconds: number | undefined = undefined
    let creditedWatchSeconds = 0
    let lastPlaybackCreditTimestampMilliseconds: number | undefined = undefined
    let playerContainerElement: HTMLDivElement | undefined = undefined
    let playbackStallTimeoutId: ReturnType<typeof setTimeout> | undefined =
        undefined

    const showAlert = useShowAlert()

    function ensureVideoJsLoaded(): Promise<VideoJsFactory> {
        if (videoJsFactory !== undefined) {
            return Promise.resolve(videoJsFactory)
        }

        if (videoJsLoadPromise !== undefined) {
            return videoJsLoadPromise
        }

        videoJsLoadPromise = Promise.all([
            import('video.js'),
            import('video.js/dist/video-js.css')
        ]).then(([videoJsModule]) => {
            videoJsFactory = videoJsModule.default
            return videoJsFactory
        })

        return videoJsLoadPromise
    }

    function clearPlaybackStallTimeout() {
        if (playbackStallTimeoutId !== undefined) {
            clearTimeout(playbackStallTimeoutId)
            playbackStallTimeoutId = undefined
        }
    }

    function getPlaybackCreditTimestampMilliseconds(): number {
        // performance.now() avoids clock changes while the modal is open, with Date.now() as a browser-safe fallback.
        if (typeof performance !== 'undefined') {
            return performance.now()
        }

        return Date.now()
    }

    function creditNormalPlaybackTime(player: Player) {
        if (
            lastPlaybackCreditTimestampMilliseconds === undefined ||
            player.paused() ||
            player.seeking()
        ) {
            return
        }

        const currentTimestampMilliseconds =
            getPlaybackCreditTimestampMilliseconds()
        const elapsedSeconds = Math.max(
            0,
            (currentTimestampMilliseconds -
                lastPlaybackCreditTimestampMilliseconds) /
                1000
        )

        lastPlaybackCreditTimestampMilliseconds = currentTimestampMilliseconds
        creditedWatchSeconds += elapsedSeconds

        // Playback credit is only used to prevent forward seeking; completion must wait for the actual ended event.
    }

    function startPlaybackCredit() {
        // Start a fresh wall-clock window whenever playback resumes after buffering, pausing, or seeking.
        lastPlaybackCreditTimestampMilliseconds =
            getPlaybackCreditTimestampMilliseconds()
    }

    function stopPlaybackCredit(player: Player) {
        // Credit the final bit of uninterrupted playback before stopping the timer.
        creditNormalPlaybackTime(player)
        lastPlaybackCreditTimestampMilliseconds = undefined
    }

    function resetPlaybackGuard() {
        // A recreated player starts from the beginning, so any partial watch credit should start over too.
        completionPositionSeconds = undefined
        creditedWatchSeconds = 0
        lastPlaybackCreditTimestampMilliseconds = undefined
    }

    function enforcePlaybackPosition(player: Player) {
        const currentTime = player.currentTime()
        if (typeof currentTime !== 'number') {
            return
        }

        const maximumAllowedPlaybackPosition =
            creditedWatchSeconds + FORWARD_SEEK_GRACE_SECONDS
        if (!alreadyWatchedVideo && completionPositionSeconds === undefined) {
            // Some streaming sources report duration late; without a duration, avoid trapping playback at a fallback.
            return
        }

        // Before the actual ended event, allow normal playback through the full duration without treating the
        // 20-second Wistia threshold as completion for this Video.js player.
        let maximumAllowedUnwatchedPlaybackPosition =
            maximumAllowedPlaybackPosition
        if (!alreadyWatchedVideo && completionPositionSeconds !== undefined) {
            maximumAllowedUnwatchedPlaybackPosition = Math.min(
                maximumAllowedPlaybackPosition,
                completionPositionSeconds
            )
        }

        if (
            currentTime <=
            maximumAllowedUnwatchedPlaybackPosition +
                PLAYBACK_POSITION_EPSILON_SECONDS
        ) {
            return
        }

        // Native browser controls can still seek under Video.js, so clamp the media element itself.
        player.currentTime(Math.max(0, maximumAllowedUnwatchedPlaybackPosition))
    }

    function enforceNormalPlaybackRate(player: Player) {
        const playbackRate = player.playbackRate()
        if (
            typeof playbackRate === 'number' &&
            playbackRate !== NORMAL_PLAYBACK_RATE
        ) {
            // Some native control surfaces expose speed controls, so force agreement videos back to real time.
            player.playbackRate(NORMAL_PLAYBACK_RATE)
        }
    }

    function preventNativeVideoContextMenu(event: MouseEvent) {
        // The browser media context menu can expose "show all controls", bypassing the Video.js control bar.
        event.preventDefault()
    }

    function getVideoAspectRatio(player: Player): number {
        const videoWidth = player.videoWidth()
        const videoHeight = player.videoHeight()

        if (
            typeof videoWidth === 'number' &&
            typeof videoHeight === 'number' &&
            videoWidth > 0 &&
            videoHeight > 0
        ) {
            return videoWidth / videoHeight
        }

        return FALLBACK_VIDEO_ASPECT_RATIO
    }

    function updatePlayerDimensions(player: Player) {
        if (typeof window === 'undefined') {
            return
        }

        const maxWidth = Math.min(
            window.innerWidth * VIDEO_VIEWPORT_PADDING_RATIO,
            MAX_VIDEO_WIDTH_PX
        )
        const maxHeight = window.innerHeight * VIDEO_VIEWPORT_PADDING_RATIO
        const aspectRatio = getVideoAspectRatio(player)

        let width = maxWidth
        let height = width / aspectRatio

        if (height > maxHeight) {
            height = maxHeight
            width = height * aspectRatio
        }

        player.width(width)
        player.height(height)
    }

    function handleWindowResize() {
        if (videoPlayer === undefined) {
            return
        }

        updatePlayerDimensions(videoPlayer)
    }

    function schedulePlaybackStallTimeout() {
        clearPlaybackStallTimeout()

        playbackStallTimeoutId = setTimeout(() => {
            if (!isWatchingVideo.value || alreadyWatchedVideo) {
                return
            }

            hideVideo()
            resetPlayer()
            showAlert(
                'Heads Up!',
                'The video appears to be stalled due to a connection issue. Please try again.'
            )
        }, 20000)
    }

    function handleWatchedAndContinue() {
        if (alreadyWatchedVideo && !hasHandledContinue) {
            hasHandledContinue = true
            handleContinue()
        }
    }

    function exitPlayerFullscreen(player: Player | undefined) {
        if (player === undefined || player.isDisposed()) {
            return
        }

        if (player.isFullWindow) {
            // Video.js can emulate fullscreen by locking the player to the browser window; exit only that player-owned state.
            player.exitFullWindow()
            return
        }

        if (player.isFullscreen()) {
            // The ended event fires before Video.js exits fullscreen, so leave the player fullscreen state instead of requiring Escape afterward.
            void player.exitFullscreen().catch(() => undefined)
        }
    }

    function hideVideo() {
        exitPlayerFullscreen(videoPlayer)

        if (playerContainerElement !== undefined) {
            playerContainerElement.style.display = 'none'
        }

        if (videoPlayer !== undefined) {
            videoPlayer.pause()
        }

        clearPlaybackStallTimeout()
        isWatchingVideo.value = false

        if (typeof document !== 'undefined') {
            document.body.classList.remove('overflow-hidden')
        }

        handleWatchedAndContinue()
    }

    function resetPlayer() {
        clearPlaybackStallTimeout()
        resetPlaybackGuard()

        if (videoPlayer !== undefined) {
            exitPlayerFullscreen(videoPlayer)
            videoPlayer.dispose()
            videoPlayer = undefined
        }
        playerReadyPromise = undefined

        if (
            playerContainerElement !== undefined &&
            playerContainerElement.parentNode !== null
        ) {
            playerContainerElement.parentNode.removeChild(
                playerContainerElement
            )
            playerContainerElement = undefined
        }

        isWatchingVideo.value = false
        if (typeof document !== 'undefined') {
            document.body.classList.remove('overflow-hidden')
        }
    }

    function showVideo() {
        if (playerContainerElement === undefined) {
            return
        }

        playerContainerElement.style.display = 'flex'
        isWatchingVideo.value = true

        if (typeof document !== 'undefined') {
            document.body.classList.add('overflow-hidden')
        }
    }

    function createPlayerReadyPromise(player: Player): Promise<void> {
        return new Promise((resolve, reject) => {
            player.ready(() => {
                const controlBar = player.getChild('controlBar')
                const progressControl =
                    controlBar !== undefined && 'getChild' in controlBar
                        ? controlBar.getChild('progressControl')
                        : undefined

                if (
                    progressControl !== undefined &&
                    'disable' in progressControl &&
                    typeof progressControl.disable === 'function'
                ) {
                    progressControl.disable()
                }

                resolve()
            })

            player.one('error', () => {
                reject(new Error('Video player failed to initialize'))
            })
        })
    }

    function updateCompletionPositionFromDuration(player: Player) {
        const duration = player.duration()
        if (
            typeof duration === 'number' &&
            !Number.isNaN(duration) &&
            Number.isFinite(duration)
        ) {
            // Scrub prevention only starts once Video.js has a real duration to clamp against.
            completionPositionSeconds = Number(Math.max(5, duration))
        }
    }

    function withTimeout<T>(
        promise: Promise<T>,
        timeoutMs: number
    ): Promise<T> {
        return new Promise((resolve, reject) => {
            const timeoutId: ReturnType<typeof setTimeout> = setTimeout(() => {
                reject(new Error('Timed out waiting for video player'))
            }, timeoutMs)

            promise.then(
                result => {
                    clearTimeout(timeoutId)
                    resolve(result)
                },
                error => {
                    clearTimeout(timeoutId)
                    reject(error)
                }
            )
        })
    }

    async function setupVideoPlayer() {
        if (
            videoUrl === null ||
            videoPlayer !== undefined ||
            typeof document === 'undefined'
        ) {
            return
        }

        const videojs = await ensureVideoJsLoaded()

        const container = document.createElement('div')
        container.style.position = 'fixed'
        container.style.inset = '0'
        container.style.background = 'rgba(0, 0, 0, 0.85)'
        container.style.display = 'none'
        container.style.alignItems = 'center'
        container.style.justifyContent = 'center'
        container.style.zIndex = '10000'

        const videoElement = document.createElement('video')
        videoElement.className = 'video-js vjs-default-skin'
        videoElement.setAttribute('playsinline', 'true')
        videoElement.setAttribute(
            'controlslist',
            'noremoteplayback nodownload noplaybackrate'
        )
        videoElement.disablePictureInPicture = true
        videoElement.addEventListener(
            'contextmenu',
            preventNativeVideoContextMenu
        )

        container.addEventListener('contextmenu', preventNativeVideoContextMenu)
        container.appendChild(videoElement)
        document.body.appendChild(container)
        playerContainerElement = container

        const mimeType = inferVideoMimeType(videoUrl)
        const player = videojs(videoElement, {
            controls: true,
            preload: 'auto',
            autoplay: false,
            responsive: true,
            fluid: false,
            playbackRates: [NORMAL_PLAYBACK_RATE],
            controlBar: {
                pictureInPictureToggle: false
            },
            sources:
                mimeType !== undefined
                    ? [{ src: videoUrl, type: mimeType }]
                    : [{ src: videoUrl }]
        })

        playerReadyPromise = createPlayerReadyPromise(player)
        resetPlaybackGuard()
        updatePlayerDimensions(player)

        player.on('loadedmetadata', () => {
            updateCompletionPositionFromDuration(player)
            updatePlayerDimensions(player)
        })

        player.on('durationchange', () => {
            updateCompletionPositionFromDuration(player)
        })

        player.on('playing', () => {
            startPlaybackCredit()
            enforcePlaybackPosition(player)
            enforceNormalPlaybackRate(player)
            clearPlaybackStallTimeout()
        })

        player.on('pause', () => {
            stopPlaybackCredit(player)
        })

        player.on('waiting', () => {
            stopPlaybackCredit(player)
            if (isWatchingVideo.value) {
                schedulePlaybackStallTimeout()
            }
        })

        player.on('stalled', () => {
            stopPlaybackCredit(player)
            if (isWatchingVideo.value) {
                schedulePlaybackStallTimeout()
            }
        })

        player.on('seeking', () => {
            stopPlaybackCredit(player)
            enforcePlaybackPosition(player)
        })

        player.on('seeked', () => {
            enforcePlaybackPosition(player)
            if (!player.paused()) {
                startPlaybackCredit()
            }
        })

        player.on('ratechange', () => {
            enforceNormalPlaybackRate(player)
        })

        player.on('timeupdate', () => {
            creditNormalPlaybackTime(player)
            enforcePlaybackPosition(player)
            if (isWatchingVideo.value) {
                clearPlaybackStallTimeout()
            }
        })

        player.on('ended', () => {
            // Only Video.js's real media completion should close and advance the agreement video.
            alreadyWatchedVideo = true
            stopPlaybackCredit(player)
            hideVideo()
        })

        player.on('error', () => {
            if (isLoadingVideo.value) {
                return
            }

            hideVideo()
            resetPlayer()
            showAlert(
                'Whoops!',
                'It appears the video encountered an error while loading. Please reload and try again.'
            )
        })

        videoPlayer = player
    }

    onMounted(() => {
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', handleWindowResize)
        }
        void setupVideoPlayer()
    })

    onUnmounted(() => {
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', handleWindowResize)
        }
        resetPlayer()
    })

    async function handleWatchVideo() {
        if (alreadyWatchedVideo) {
            handleContinue()
            return
        }

        if (!validateContinue()) {
            return
        }

        if (videoUrl === null) {
            handleContinue()
            return
        }

        isLoadingVideo.value = true
        try {
            await setupVideoPlayer()

            if (playerReadyPromise === undefined) {
                throw new Error('Video player was not created')
            }

            await withTimeout(playerReadyPromise, 10000)
            if (videoPlayer === undefined) {
                throw new Error('Video player was not created')
            }

            updatePlayerDimensions(videoPlayer)
            showVideo()
        } catch (_error) {
            console.error(_error)
            hideVideo()
            resetPlayer()
            showAlert(
                'Whoops!',
                'It appears the video is having trouble loading. Please reload and try again.'
            )
            return
        } finally {
            isLoadingVideo.value = false
        }
    }

    function handleAlreadyWatched() {
        alreadyWatchedVideo = true
        hideVideo()
    }

    const showAlreadyWatchButton = computed(
        () => isWatchingVideo.value && allowSkippingVideo
    )

    return {
        isLoadingVideo,
        isWatchingVideo,
        showAlreadyWatchButton,
        handleWatchVideo,
        handleAlreadyWatched
    }
}
